import React, { useState, useEffect } from 'react';
import api, { fetchAllPages } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import SearchSelect from '@/components/common/SearchSelect';
import { PARTICIPANT_SEARCH_HINT } from '@/components/common/pickerLabels';
import { toast } from 'sonner';
import { Plus, Car, Calendar, FileText, Edit2 } from 'lucide-react';

export default function HouseVehiclesTab({ house, houseId }) {
  const { isAdmin } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    registration: '',
    model: '',
    service_due_date: '',
    rego_expiry: '',
    key_location: '',
    notes: '',
    participant_id: ''
  });
  const [participants, setParticipants] = useState([]);

  useEffect(() => {
    fetchVehicles();
  }, [houseId]);

  useEffect(() => {
    (async () => {
      try {
        const items = await fetchAllPages('/participants');   // pageSize=500 exceeded the max of 100 → 422, empty list
        setParticipants(Array.isArray(items) ? items : []);
      } catch { /* participants optional */ }
    })();
  }, []);

  const participantName = (id) => {
    if (!id) return '-';
    const p = participants.find(x => (x.id || x._id) === id);
    if (!p) return id.slice(0, 8) + '…';
    return `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.full_name || p.name || id;
  };

  const fetchVehicles = async () => {
    try {
      const response = await api.get(`/vehicles?house_id=${houseId}`);
      // Filter to only show vehicles for this house
      const houseVehicles = (response.data.data || []).filter(v => v.house_id === houseId);
      setVehicles(houseVehicles);
    } catch (error) {
      toast.error('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (vehicle = null) => {
    if (vehicle) {
      setEditingVehicle(vehicle);
      setFormData({
        name: vehicle.name || '',
        registration: vehicle.registration || '',
        model: vehicle.model || '',
        service_due_date: vehicle.service_due_date || '',
        rego_expiry: vehicle.rego_expiry || '',
        key_location: vehicle.key_location || '',
        notes: vehicle.notes || '',
        participant_id: vehicle.participant_id || ''
      });
    } else {
      setEditingVehicle(null);
      setFormData({
        name: '',
        registration: '',
        model: '',
        service_due_date: '',
        rego_expiry: '',
        key_location: '',
        notes: '',
        participant_id: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingVehicle) {
        await api.put(`/vehicles/${editingVehicle.id}`, formData);
        toast.success('Vehicle updated successfully');
      } else {
        await api.post('/vehicles', {
          ...formData,
          house_id: houseId
        });
        toast.success('Vehicle created successfully');
      }
      setDialogOpen(false);
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Failed to save vehicle');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-AU');
  };

  const isExpiringSoon = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const today = new Date();
    const daysUntil = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
    return daysUntil <= 30 && daysUntil >= 0;
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  }

  return (
    <Card data-testid="house-vehicles-tab">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Car className="h-5 w-5" />
            House Vehicles
          </CardTitle>
          <CardDescription>{isAdmin ? 'Manage vehicles assigned to this house' : 'View vehicles assigned to this house'}</CardDescription>
        </div>
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => handleOpenDialog()} data-testid="add-vehicle-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Vehicle
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <form onSubmit={handleSubmit}>
                <DialogHeader>
                  <DialogTitle>{editingVehicle ? 'Edit Vehicle' : 'Add New Vehicle'}</DialogTitle>
                  <DialogDescription>
                    {editingVehicle ? 'Update vehicle details' : 'Add a new vehicle to this house'}
                  </DialogDescription>
                </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Vehicle Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., House Van"
                      required
                      data-testid="vehicle-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="registration">Registration *</Label>
                    <Input
                      id="registration"
                      value={formData.registration}
                      onChange={(e) => setFormData({ ...formData, registration: e.target.value })}
                      placeholder="e.g., ABC123"
                      required
                      data-testid="vehicle-registration-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="model">Make/Model</Label>
                  <Input
                    id="model"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    placeholder="e.g., Toyota HiAce 2020"
                    data-testid="vehicle-model-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="service_due_date">Service Due Date</Label>
                    <Input
                      id="service_due_date"
                      type="date"
                      value={formData.service_due_date}
                      onChange={(e) => setFormData({ ...formData, service_due_date: e.target.value })}
                      data-testid="vehicle-service-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rego_expiry">Registration Expiry</Label>
                    <Input
                      id="rego_expiry"
                      type="date"
                      value={formData.rego_expiry}
                      onChange={(e) => setFormData({ ...formData, rego_expiry: e.target.value })}
                      data-testid="vehicle-rego-expiry-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="key_location">Key location</Label>
                  <Input
                    id="key_location"
                    value={formData.key_location}
                    onChange={(e) => setFormData({ ...formData, key_location: e.target.value })}
                    placeholder="e.g. Key hook inside pantry door"
                    data-testid="vehicle-key-location-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="participant_id">Belongs to participant (optional)</Label>
                  <SearchSelect
                    options={participants.map((p) => ({ value: p.id || p._id, label: `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.full_name || p.name || (p.id || p._id), keywords: p.ndis_number || '' }))}
                    value={formData.participant_id}
                    onChange={(v) => setFormData({ ...formData, participant_id: v })}
                    placeholder="— Company / house vehicle —"
                    searchPlaceholder={PARTICIPANT_SEARCH_HINT}
                    emptyText="No participants match that search"
                    clearLabel="— Company / house vehicle —"
                    testId="vehicle-participant-select"
                  />
                  <p className="text-xs text-muted-foreground">Link this vehicle to a participant to list it under Transport · Vehicles · Participant.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Input
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes"
                    data-testid="vehicle-notes-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-vehicle-btn">
                  {editingVehicle ? 'Update' : 'Create'} Vehicle
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </CardHeader>
      <CardContent>
        {vehicles.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground" data-testid="no-vehicles-message">
            <Car className="h-12 w-12 mx-auto mb-4 opacity-20" />
            <p>No vehicles assigned to this house</p>
            <p className="text-sm mt-1">Click "Add Vehicle" to add one</p>
          </div>
        ) : (
          <Table data-testid="vehicles-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Registration</TableHead>
                <TableHead>Make/Model</TableHead>
                <TableHead>Participant</TableHead>
                <TableHead>Service Due</TableHead>
                <TableHead>Rego Expiry</TableHead>
                <TableHead>Key location</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vehicles.map((vehicle) => (
                <TableRow key={vehicle.id} data-testid={`vehicle-row-${vehicle.id}`}>
                  <TableCell className="font-medium">{vehicle.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{vehicle.registration}</Badge>
                  </TableCell>
                  <TableCell>{vehicle.model || '-'}</TableCell>
                  <TableCell data-testid={`vehicle-participant-${vehicle.id}`}>{vehicle.participant_name || participantName(vehicle.participant_id)}</TableCell>
                  <TableCell>
                    {vehicle.service_due_date ? (
                      <span className={`flex items-center gap-1 ${isExpired(vehicle.service_due_date) ? 'text-red-500' : isExpiringSoon(vehicle.service_due_date) ? 'text-amber-500' : ''}`}>
                        <Calendar className="h-3 w-3" />
                        {formatDate(vehicle.service_due_date)}
                        {isExpired(vehicle.service_due_date) && <Badge variant="destructive" className="ml-1 text-xs">Overdue</Badge>}
                        {isExpiringSoon(vehicle.service_due_date) && !isExpired(vehicle.service_due_date) && <Badge variant="warning" className="ml-1 text-xs bg-amber-500">Soon</Badge>}
                      </span>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {vehicle.rego_expiry ? (
                      <span className={`flex items-center gap-1 ${isExpired(vehicle.rego_expiry) ? 'text-red-500' : isExpiringSoon(vehicle.rego_expiry) ? 'text-amber-500' : ''}`}>
                        <FileText className="h-3 w-3" />
                        {formatDate(vehicle.rego_expiry)}
                        {isExpired(vehicle.rego_expiry) && <Badge variant="destructive" className="ml-1 text-xs">Expired</Badge>}
                        {isExpiringSoon(vehicle.rego_expiry) && !isExpired(vehicle.rego_expiry) && <Badge variant="warning" className="ml-1 text-xs bg-amber-500">Soon</Badge>}
                      </span>
                    ) : '-'}
                  </TableCell>
                  <TableCell className="max-w-[150px] truncate">{vehicle.key_location || '-'}</TableCell>
                  <TableCell className="max-w-[150px] truncate">{vehicle.notes || '-'}</TableCell>
                  <TableCell>
                    {isAdmin ? (
                      <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(vehicle)} data-testid={`edit-vehicle-${vehicle.id}`}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    ) : (
                      <span className="text-xs text-muted-foreground">View only</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
